import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    Filter,
    ChevronDown,
    MoreVertical,
    Shield,
    Folder,
    FolderPlus,
    FileText,
    Plus,
    X,
    Edit,
    Trash2,
    Globe,
    Home,
    Building2,
    Settings,
    AlertCircle,
    Check,
    ArrowUpDown,
    Eye,
    InfoIcon,
    Languages,
    MapPin,
    Star,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { CategoryData } from "../../modules";
import {
    createUserCategories,
    deleteUserCategories,
    getUserCategories,
    updateUserCategories,
    useAppDispatch,
} from "../../redux";
import { bulkImportCategories, downloadCategoryTemplate } from "../../redux/Actions/AdminAction";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import Spinner from "../../modules/shared/components/Spinner";
import CategoryTranslationDialog from "./CategoryTranslationDialog";
import CategoryImportModal from "./CategoryImportModal";
import { exportCategoriesToCSV, downloadCSV } from "../../utils/categoryCSVUtils";

const ServiceCategories = () => {
    // State management
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [selectedType, setSelectedType] = useState("all");
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [editCategoryIndex, setEditCategoryIndex] = useState(null);
    const [sortBy, setSortBy] = useState("name");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [showTranslationDialog, setShowTranslationDialog] = useState(false);
    const [translationCategory, setTranslationCategory] = useState<any>(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const { categories: serverCategories } = useSelector((state: any) => {

        return state.admin;
    });
    const isLoading = useSelector(selectLoaderValue);
    const dispatch = useAppDispatch();

    // Load categories on component mount
    useEffect(() => {
        if (isInitialLoad) {
            getCategoryData(currentPage, limit);
            setIsInitialLoad(false);
        }
    }, []);

    // Update pagination when categories data changes
    useEffect(() => {
        if (serverCategories) {
            // Use server-side pagination data
            const totalCount = serverCategories.totalCount || 0;
            const calculatedTotalPages = Math.ceil(totalCount / limit);
            setTotalPages(calculatedTotalPages);

        }
    }, [serverCategories, limit]);

    // Fetch data when page changes
    useEffect(() => {
        if (!isInitialLoad) {
            getCategoryData(currentPage, limit);
        }
    }, [currentPage, limit, isInitialLoad]);

    // Debounced search effect - trigger after 2 characters
    useEffect(() => {
        if (searchQuery.length >= 2 || searchQuery.length === 0) {
            const timeoutId = setTimeout(() => {
                if (!isInitialLoad) {
                    setCurrentPage(1);
                    getCategoryData(1, limit);
                }
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [searchQuery, isInitialLoad]);

    // Reset to page 1 when type filter changes
    useEffect(() => {
        if (!isInitialLoad) {
            setCurrentPage(1);
            getCategoryData(1, limit);
        }
    }, [selectedType, isInitialLoad]);



    // Filter and process categories data
    const categories = useMemo(() => {
        if (!serverCategories || !serverCategories.data || serverCategories.data.length === 0) {
            return []; // Return empty array if no server categories
        }

        // Since we're now doing server-side filtering, we just need to map the data
        return serverCategories.data.map((cat: any) => ({
            id: cat.id,
            name: cat.name,
            type: { id: cat.type?.id },
            description: cat.description,
            status: "active", // You might want to get this from server data if available
            totalProviders: 0, // These fields would ideally come from server
            activeProviders: 0,
            subcategories: cat.subcategories || [],
            requirements: cat.requirements || [],
            verificationRequired: cat.isRequireVerification,
            emergencyService: cat.isEmergency,
            averageRating: 0,
            totalJobs: 0,
            icon: "", // Could be mapped from server data if available
            translatedName: cat.translatedName || {},
            translatedSubCategories: cat.translatedSubCategories || {},
        }));
    }, [serverCategories]);
    const buildCategoryPayload = (data: any) => ({
        name: data.name,
        description: data.description,
        requirements: data.requirements || [],
        subcategories: data.subcategories || [],
        isEmergency: data?.emergencyService,
        isRequireVerification: data?.verificationRequired,
        type: {
            id: Number(data.type?.id || data.type), // Wrap the enum number inside an object with key `id`
        },
        translatedName: data.translatedName || {},
        translatedSubCategories: data.translatedSubCategories || {},
    });

    const handleCategorySave = async (data: any & { id?: string }) => {
        const payload = buildCategoryPayload(data);

        if (!data.id) {
            // Create new category
            await dispatch(createUserCategories({ data: payload }));
        } else {
            // Update existing category
            await dispatch(updateUserCategories({ id: data.id, data: payload }));
        }
    };
    const getCategoryData = async (page: number = 1, limit: number = 10) => {
        const filters: any = {};

        // Add search keyword if 2+ characters
        if (searchQuery && searchQuery.length >= 2) {
            filters.keyword = searchQuery;
        }

        // Add type filter if selected
        if (selectedType !== "all") {
            let typeId;
            switch (selectedType) {
                case "home":
                    typeId = 1;
                    break;
                case "professional":
                    typeId = 2;
                    break;
                case "international":
                    typeId = 3;
                    break;
                default:
                    typeId = 1;
            }
            filters.type = [typeId.toString()];
        }

        const result = await dispatch(getUserCategories({
            page,
            limit,
            filters,
            isShowLoader: false
        }));

        console.log("Categories API Response:", result);
    };
    const emptyCategory = (): CategoryData => ({
        name: "",
        type: { id: "1" }, // Default to Local
        description: "",
        requirements: [],
        subcategories: [], // Added subcategories array
        verificationRequired: false,
        emergencyService: false,
    });

    // CSV Import/Export Handlers
    const handleDownloadTemplate = async () => {
        try {
            await downloadCategoryTemplate();
        } catch (error) {
            console.error("Error downloading template:", error);
        }
    };

    const handleExportCurrentCategories = () => {
        if (!categories || categories.length === 0) {
            alert("No categories to export");
            return;
        }
        const csvContent = exportCategoriesToCSV(categories);
        downloadCSV(csvContent, `categories_export_${new Date().toISOString().split("T")[0]}.csv`);
    };

    const handleBulkImport = async (file: File): Promise<{
        success: number;
        failed: number;
        errors: Array<{ categoryName: string; error: string }>;
        created: number;
        updated: number;
    }> => {
        try {
            const result = await bulkImportCategories(file);

            // Refresh the categories list
            await getCategoryData(currentPage, limit);

            return result;
        } catch (error: any) {
            console.error("Error importing categories:", error);
            return {
                success: 0,
                failed: 1,
                errors: [{ categoryName: "Import Error", error: error.message || "Unknown error" }],
                created: 0,
                updated: 0,
            };
        }
    };

    const CategoryModal = ({ open, onClose, onSave, initialData }) => {
        const [category, setCategory] = useState<CategoryData>(emptyCategory());
        const [requirementInput, setRequirementInput] = useState<string>("");
        const [subcategoryInput, setSubcategoryInput] = useState<string>("");
        const [errors, setErrors] = useState<{ [key: string]: string }>({});
        useEffect(() => {
            if (initialData) {
                setCategory(initialData);
            } else {
                setCategory(emptyCategory());
            }
            setRequirementInput("");
            setSubcategoryInput("");
            setErrors({});
        }, [open, initialData]);

        const handleChange = (field: keyof CategoryData, value: any) => {
            setCategory(prev => ({
                ...prev,
                [field]: value,
            }));
        };

        const handleSettingChange = (field: "verificationRequired" | "emergencyService", value: boolean) => {
            setCategory(prev => ({
                ...prev,
                [field]: value,
            }));
        };

        const handleAddRequirement = () => {
            const input = requirementInput.trim();
            if (!input) return;
            setCategory(prev => ({
                ...prev,
                requirements: [...prev.requirements, input],
            }));
            setRequirementInput("");
        };

        const handleRemoveRequirement = (index: number) => {
            setCategory(prev => {
                const updated = [...prev.requirements];
                updated.splice(index, 1);
                return { ...prev, requirements: updated };
            });
        };

        const handleAddSubcategory = () => {
            const input = subcategoryInput.trim();
            if (!input) return;
            setCategory(prev => ({
                ...prev,
                subcategories: [...(prev.subcategories || []), input],
            }));
            setSubcategoryInput("");
        };

        const handleRemoveSubcategory = (index: number) => {
            setCategory(prev => {
                const updated = [...(prev.subcategories || [])];
                updated.splice(index, 1);
                return { ...prev, subcategories: updated };
            });
        };

        const validate = (): boolean => {
            const errs: { [key: string]: string } = {};
            if (!category.name.trim()) errs.name = "Category name is required";
            if (!category.type?.id) errs.type = "Category type is required";
            if (!category.description.trim()) errs.description = "Description is required";
            if (!category.requirements.length) errs.requirements = "At least two requirement is required";
            // Add validation for subcategories if needed
            setErrors(errs);
            return Object.keys(errs).length === 0;
        };

        const handleSubmit = (e: any) => {
            e.preventDefault();
            if (!validate()) return;
            onSave(category);
            onClose();
        };

        if (!open) return null;

        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.95 }}
                    className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6 border-b sticky top-0 bg-white" style={{ textAlign: "left" }}>
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-semibold">
                                {initialData ? "Edit Category" : "Add New Category"}
                            </h2>
                            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {/* Basic Information */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Category Name</label>
                                <Input
                                    value={category.name}
                                    onChange={e => handleChange("name", e.target.value)}
                                    placeholder="Enter category name"
                                    className="mt-1"
                                />
                                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Type</label>
                                <select
                                    value={category.type?.id}
                                    onChange={e => handleChange("type", { id: e.target.value })}
                                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                                    <option value="1">Home Services</option>
                                    <option value="2">Professional Services</option>
                                    <option value="3">International Services</option>
                                </select>
                                {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    value={category.description}
                                    onChange={e => handleChange("description", e.target.value)}
                                    placeholder="Enter category description"
                                    rows={3}
                                    className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                                {errors.description && (
                                    <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                                )}
                            </div>
                        </div>

                        {/* Subcategories */}
                        <div className="space-y-4">
                            <h3 className="font-medium">Subcategories</h3>
                            <div className="flex gap-2">
                                <Input
                                    value={subcategoryInput}
                                    onChange={e => setSubcategoryInput(e.target.value)}
                                    placeholder="Add subcategory"
                                />
                                <Button type="button" onClick={handleAddSubcategory} variant="outline">
                                    Add
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {category.subcategories?.map((sub, index) => (
                                    <Badge
                                        variant={undefined}
                                        key={index}
                                        className="bg-blue-100 text-blue-700 flex items-center gap-1">
                                        {sub}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveSubcategory(index)}
                                            className="ml-1 hover:bg-blue-200 rounded-full p-0.5">
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        {/* Requirements */}
                        <div className="space-y-4">
                            <h3 className="font-medium">Requirements</h3>
                            <div className="flex gap-2">
                                <Input
                                    value={requirementInput}
                                    onChange={e => setRequirementInput(e.target.value)}
                                    placeholder="Add requirement"
                                />
                                <Button type="button" onClick={handleAddRequirement} variant="outline">
                                    Add
                                </Button>
                            </div>
                            {errors.requirements && <p className="text-red-500 text-sm">{errors.requirements}</p>}
                            <div className="flex flex-wrap gap-2">
                                {category.requirements.map((req, index) => (
                                    <Badge
                                        variant={undefined}
                                        key={index}
                                        className="bg-gray-100 text-gray-700 flex items-center gap-1">
                                        {req}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveRequirement(index)}
                                            className="ml-1 hover:bg-gray-200 rounded-full p-0.5">
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        </div>

                        {/* Additional Settings */}
                        <div className="space-y-4">
                            <h3 className="font-medium">Settings</h3>
                            <div className="flex flex-col gap-4">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={category.verificationRequired || false}
                                        onChange={e => handleSettingChange("verificationRequired", e.target.checked)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span>Require Verification</span>
                                </label>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={category.emergencyService || false}
                                        onChange={e => handleSettingChange("emergencyService", e.target.checked)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span>Emergency Service Available</span>
                                </label>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button type="submit">{initialData ? "Save Changes" : "Create Category"}</Button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        );
    };



    // Category Details Card Component
    const CategoryDetailsCard = ({ category }) => (
        <Card className="hover:shadow-md transition-shadow">
            <div className="p-6 space-y-4" style={{ textAlign: "left" }}>
                {/* Header */}
                <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                        <div
                            className={`p-2 rounded-lg ${Number(category.type?.id) === 1
                                    ? "bg-blue-100"
                                    : Number(category.type?.id) === 2
                                        ? "bg-green-100"
                                        : "bg-purple-100"
                                }`}>
                            {Number(category.type?.id) === 1 ? (
                                <Home className="h-5 w-5 text-blue-600" />
                            ) : Number(category.type?.id) === 2 ? (
                                <Building2 className="h-5 w-5 text-green-600" />
                            ) : (
                                <Globe className="h-5 w-5 text-purple-600" />
                            )}
                        </div>
                        <div>
                            <h3 className="font-semibold text-lg">{category.name}</h3>
                            <div className="flex gap-2 flex-wrap">
                                <Badge
                                    variant={undefined}
                                    className={`${Number(category.type?.id) === 1
                                            ? "bg-blue-100 text-blue-700"
                                            : Number(category.type?.id) === 2
                                                ? "bg-green-100 text-green-700"
                                                : "bg-purple-100 text-purple-700"
                                        }`}>
                                    {Number(category.type?.id) === 1
                                        ? "Home Services"
                                        : Number(category.type?.id) === 2
                                            ? "Professional Services"
                                            : "International Services"}
                                </Badge>
                                {category.translatedName && Object.keys(category.translatedName).length > 0 && (
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                        <Languages className="h-3 w-3 mr-1" />
                                        {Object.keys(category.translatedName).length} {Object.keys(category.translatedName).length === 1 ? "Translation" : "Translations"}
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                console.log("Opening translation dialog for category:", category);
                                setTranslationCategory(category);
                                setShowTranslationDialog(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Manage Translations">
                            <Languages className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setEditCategoryIndex(category);
                                setShowCategoryModal(true);
                            }}
                            className="text-gray-600 hover:text-gray-900">
                            <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-900"
                            onClick={async () => {
                                const confirmed = window.confirm(
                                    `Are you sure you want to delete the category "${category.name}"?`
                                );
                                if (!confirmed) return;
                                await dispatch(deleteUserCategories({ id: category.id }));
                            }}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Description */}
                <p className="text-gray-600">{category.description}</p>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm text-gray-500">Total Providers</div>
                        <div className="mt-1 font-semibold">{category.totalProviders}</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm text-gray-500">Active Providers</div>
                        <div className="mt-1 font-semibold">{category.activeProviders}</div>
                    </div>
                </div>

                {/* Subcategories */}
                <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Subcategories</h4>
                    <div className="flex flex-wrap gap-2">
                        {category.subcategories.map((sub, index) => (
                            <Badge key={index} variant="secondary">
                                {sub}
                            </Badge>
                        ))}
                    </div>
                </div>

                {/* Requirements */}
                <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Requirements</h4>
                    <div className="space-y-2">
                        {category.requirements.map((req, index) => (
                            <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                <Check className="h-4 w-4 text-green-500" />
                                {req}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Additional Info */}
                <div className="flex flex-wrap gap-4 pt-4 border-t">
                    <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-400" />
                        <span className="text-sm text-gray-600">{category.averageRating} Rating</span>
                    </div>
                    <div className="flexitems-center gap-2">
                        <FileText className="h-4 w-4 text-blue-400" />
                        <span className="text-sm text-gray-600">{category.totalJobs} Total Jobs</span>
                    </div>
                    {category.verificationRequired && (
                        <Badge variant="outline" className="border-green-200 text-green-700">
                            Verification Required
                        </Badge>
                    )}
                    {category.emergencyService && (
                        <Badge variant="outline" className="border-red-200 text-red-700">
                            Emergency Service
                        </Badge>
                    )}
                </div>
            </div>
        </Card>
    );

    return (
        <>
            {isLoading && (
                <div className="fixed left-[50%] top-[40%] z-[1000px]">
                    <Spinner color="green" />
                </div>
            )}
            <div
                className={`min-h-screen bg-gray-50 flex flex-col ${isLoading ? "opacity-25" : "opacity-100"}`}
                style={{ textAlign: "left" }}>
                <AdminHeader />

                <main className="flex-1 lg:pl-64 pt-16">
                    <div className="p-6">
                        <div className="max-w-7xl mx-auto space-y-6">
                            {/* Header */}
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">Service Categories</h1>
                                    <p className="text-gray-600 mt-1">Manage and organize service categories</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        onClick={() => setShowImportModal(true)}
                                        variant="outline"
                                        className="flex items-center gap-2">
                                        <FileText className="h-4 w-4" />
                                        Import CSV
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setShowCategoryModal(true);
                                            setEditCategoryIndex(null);
                                        }}
                                        className="flex items-center gap-2">
                                        <Plus className="h-4 w-4" />
                                        Add Category
                                    </Button>
                                </div>
                            </div>

                            {/* Search and Filters */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search categories..."
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={selectedType}
                                        onChange={e => setSelectedType(e.target.value)}
                                        className="px-4 py-2 border rounded-lg">
                                        <option value="all">All Types</option>
                                        <option value="home">Home</option>
                                        <option value="professional">Professional</option>
                                        <option value="international">International</option>
                                    </select>
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowFilters(!showFilters)}
                                        className="flex items-center gap-2">
                                        <Filter className="h-5 w-5" />
                                        Filters
                                    </Button>
                                </div>
                            </div>

                            {/* Categories Grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {categories.map(category => (
                                    <CategoryDetailsCard key={category.id} category={category} />
                                ))}
                            </div>

                            {/* Pagination */}
                            {!isLoading && serverCategories && serverCategories.data && serverCategories.data.length > 0 && (
                                <div className="px-6 py-4 border-t flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-700">
                                            Showing {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, serverCategories.totalCount)} of {serverCategories.totalCount} categories
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            (Page {currentPage} of {Math.ceil(serverCategories.totalCount / limit)})
                                        </span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1 || isLoading}
                                        >
                                            Previous
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(prev => prev + 1)}
                                            disabled={!serverCategories.hasNextPage || isLoading}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Empty State */}
                            {(!serverCategories || !serverCategories.data || serverCategories.data.length === 0) && (
                                <div className="text-center py-12">
                                    <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
                                    <p className="text-gray-500 mb-4">
                                        Get started by creating a new service category.
                                    </p>
                                    <Button
                                        onClick={() => {
                                            setShowCategoryModal(true);
                                            setEditCategoryIndex(null);
                                        }}>
                                        Add Category
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </main>

                {/* Modals */}
                <AnimatePresence>
                    <CategoryModal
                        open={showCategoryModal}
                        onClose={() => setShowCategoryModal(false)}
                        onSave={handleCategorySave}
                        initialData={editCategoryIndex}
                    />
                </AnimatePresence>

                {/* Translation Dialog */}
                {translationCategory && (
                    <CategoryTranslationDialog
                        isOpen={showTranslationDialog}
                        onClose={() => {
                            setShowTranslationDialog(false);
                            setTranslationCategory(null);
                        }}
                        categoryName={translationCategory.name}
                        currentTranslations={{
                            translatedName: translationCategory.translatedName || {},
                            translatedSubCategories: translationCategory.translatedSubCategories || {},
                        }}
                        originalSubcategories={translationCategory.subcategories || []}
                        onSave={async (translations) => {
                            // Build proper payload with type conversion
                            const updatedCategory = {
                                ...translationCategory,
                                translatedName: translations.translatedName,
                                translatedSubCategories: translations.translatedSubCategories,
                            };
                            const payload = buildCategoryPayload(updatedCategory);

                            console.log("Saving translation payload:", payload);

                            // Update category with new translations
                            const result = await dispatch(
                                updateUserCategories({
                                    id: translationCategory.id,
                                    data: payload,
                                })
                            );

                            console.log("Update result:", result);

                            // Refresh categories to get updated data
                            await getCategoryData(currentPage, limit);

                            // Update the translation category state with new translations
                            // so the dialog shows the updated data immediately
                            setTranslationCategory({
                                ...translationCategory,
                                translatedName: translations.translatedName,
                                translatedSubCategories: translations.translatedSubCategories,
                            });
                        }}
                    />
                )}

                {/* Import Modal */}
                <CategoryImportModal
                    isOpen={showImportModal}
                    onClose={() => setShowImportModal(false)}
                    onImport={handleBulkImport}
                    onDownloadTemplate={handleDownloadTemplate}
                    onExportCurrent={handleExportCurrentCategories}
                />

                {/* <SharedFooter3 /> */}
            </div>
        </>
    );
};

export default ServiceCategories;
